// กลุ่ม (สุ่มขั้น 1) และกลุ่มย่อย (สุ่มขั้น 2) ตาม docs/PLAN.md ข้อ 1
// กลุ่มย่อยต้องกำหนดได้จากอนุกรมวิธานเท่านั้น เพื่อให้ script นำเข้าจัดชนิดได้อัตโนมัติ

export type ArchetypeId =
  | "MAMMAL"
  | "BIRD"
  | "REPTILE"
  | "AMPHIBIAN"
  | "FISH"
  | "INSECT"
  | "SOCIAL_INSECT"
  | "ARACHNID"
  | "CRUSTACEAN"
  | "MYRIAPOD"
  | "MOLLUSC"
  | "WORM"
  | "SESSILE"
  | "ECHINODERM";

// archetype ที่มีเนื้อหาพร้อมแล้ว ชนิดอื่นอยู่ใน D1 ได้แต่ยังไม่ถูกนำมาสุ่ม
export const ENABLED_ARCHETYPES: ReadonlySet<ArchetypeId> = new Set<ArchetypeId>([
  "MAMMAL",
  "BIRD",
  "REPTILE",
  "AMPHIBIAN",
  "FISH",
  "INSECT",
  // เฟส 3
  "SOCIAL_INSECT",
  "ARACHNID",
  "CRUSTACEAN",
  "MYRIAPOD",
  "MOLLUSC",
  "WORM",
  "SESSILE",
  "ECHINODERM",
]);

export type TaxonRank = "phylum" | "class" | "order" | "family" | "genus";

// ชนิดเข้ากลุ่มนี้ถ้าชื่อในระดับใดระดับหนึ่งตรงกับรายการ
export type TaxonMatch = Partial<Record<TaxonRank, readonly string[]>>;

export interface SubgroupDef {
  id: string;
  nameTh: string;
  match?: TaxonMatch;
  archetype?: ArchetypeId; // ถ้าไม่ระบุ ใช้ archetype ของกลุ่ม
  weight?: number; // ถ้าไม่ระบุ = 1
}

export interface GroupDef {
  id: string;
  nameTh: string;
  weight: number;
  phase: 1 | 3; // เฟสที่วางแผนไว้ (ข้อมูลอ้างอิง) จะเปิดสุ่มจริงเมื่อ archetype อยู่ใน ENABLED_ARCHETYPES
  archetype: ArchetypeId;
  match: TaxonMatch;
  subgroups: readonly SubgroupDef[]; // ตรวจตามลำดับ ตัวแรกที่ตรงชนะ
  fallback: string; // กลุ่มย่อยเมื่อไม่ตรงกับตัวไหนเลย
}

const SNAKE_FAMILIES = [
  "Acrochordidae", "Ahaetuliidae", "Aniliidae", "Anomalepididae", "Anomochilidae",
  "Atractaspididae", "Boidae", "Bolyeriidae", "Calabariidae", "Calamariidae",
  "Candoiidae", "Charinidae", "Colubridae", "Cyclocoridae", "Cylindrophiidae",
  "Dipsadidae", "Elachistodontidae", "Elapidae", "Erycidae", "Gerrhopilidae",
  "Grayiidae", "Homalopsidae", "Lamprophiidae", "Leptotyphlopidae", "Loxocemidae",
  "Natricidae", "Pareidae", "Pareatidae", "Prosymnidae", "Psammophiidae",
  "Pseudaspididae", "Pseudoxyrhophiidae", "Pythonidae", "Sanziniidae", "Sibynophiidae",
  "Tropidophiidae", "Typhlopidae", "Ungaliophiidae", "Uropeltidae", "Viperidae",
  "Xenodermidae", "Xenodermatidae", "Xenopeltidae", "Xenophidiidae", "Xenotyphlopidae",
];

const CETACEAN_FAMILIES = [
  "Balaenidae", "Balaenopteridae", "Delphinidae", "Eschrichtiidae", "Iniidae",
  "Kogiidae", "Lipotidae", "Monodontidae", "Neobalaenidae", "Phocoenidae",
  "Physeteridae", "Platanistidae", "Pontoporiidae", "Ziphiidae",
];

// อันดับในกลุ่ม Percomorpha (ปลากะพง ปลากัด ปลานิล ปลาทู ปลาปักเป้า ฯลฯ)
const PERCOMORPH_ORDERS = [
  "Acanthuriformes", "Anabantiformes", "Atheriniformes", "Batrachoidiformes",
  "Beloniformes", "Blenniiformes", "Carangiformes", "Centrarchiformes",
  "Chaetodontiformes", "Cichliformes", "Cyprinodontiformes", "Ephippiformes",
  "Gerreiformes", "Gobiesociformes", "Gobiiformes", "Istiophoriformes",
  "Kurtiformes", "Labriformes", "Lobotiformes", "Lophiiformes", "Lutjaniformes",
  "Moroniformes", "Mugiliformes", "Ophidiiformes", "Pempheriformes", "Perciformes",
  "Pleuronectiformes", "Priacanthiformes", "Scombriformes", "Scorpaeniformes",
  "Spariformes", "Synbranchiformes", "Syngnathiformes", "Tetraodontiformes",
  "Uranoscopiformes",
];

export const TERMITE_FAMILIES = [
  "Archotermopsidae", "Heterotermitidae", "Hodotermitidae", "Kalotermitidae",
  "Mastotermitidae", "Psammotermitidae", "Rhinotermitidae", "Serritermitidae",
  "Stolotermitidae", "Stylotermitidae", "Termitidae", "Termitogetonidae", "Termopsidae",
];

// ผึ้งและต่อที่อยู่เป็นสังคม (มีนางพญา ผึ้งงาน) ใช้ archetype SOCIAL_INSECT
export const SOCIAL_INSECT_GENERA: ReadonlySet<string> = new Set([
  "Apis", "Bombus", "Dolichovespula", "Melipona", "Polistes", "Polybia", "Provespa",
  "Ropalidia", "Scaptotrigona", "Tetragonula", "Trigona", "Vespa", "Vespula",
]);

// กลุ่มย่อยที่บางชนิดใช้ archetype ต่างจากของกลุ่มย่อย (ดู classify) ใช้สร้างชนิดตัวอย่างในเทสต์และตัวจำลอง
export const ARCHETYPE_VARIANTS: Readonly<Record<string, readonly ArchetypeId[]>> = {
  "insect.bee_wasp": ["SOCIAL_INSECT"],
};

// อันดับแมลงที่เปลี่ยนรูปร่างแบบสมบูรณ์ (ไข่ → หนอน → ดักแด้ → ตัวเต็มวัย)
export const COMPLETE_METAMORPHOSIS_ORDERS: ReadonlySet<string> = new Set([
  "Coleoptera", "Diptera", "Hymenoptera", "Lepidoptera", "Mecoptera", "Megaloptera",
  "Neuroptera", "Raphidioptera", "Siphonaptera", "Strepsiptera", "Trichoptera",
]);

export const GROUPS: readonly GroupDef[] = [
  {
    id: "mammal",
    nameTh: "สัตว์เลี้ยงลูกด้วยนม",
    weight: 14,
    phase: 1,
    archetype: "MAMMAL",
    match: { class: ["Mammalia"] },
    subgroups: [
      { id: "mammal.whale", nameTh: "วาฬและโลมา", match: { order: ["Cetacea"], family: CETACEAN_FAMILIES } },
      { id: "mammal.bat", nameTh: "ค้างคาว", match: { order: ["Chiroptera"] } },
      { id: "mammal.rodent", nameTh: "สัตว์ฟันแทะ", match: { order: ["Rodentia"] } },
      { id: "mammal.carnivore", nameTh: "สัตว์กินเนื้อ", match: { order: ["Carnivora"] } },
      { id: "mammal.ungulate", nameTh: "สัตว์กีบ", match: { order: ["Artiodactyla", "Cetartiodactyla", "Perissodactyla"] } },
      { id: "mammal.primate", nameTh: "ไพรเมต", match: { order: ["Primates"] } },
      {
        id: "mammal.marsupial",
        nameTh: "สัตว์มีกระเป๋าหน้าท้อง",
        match: {
          order: [
            "Dasyuromorphia", "Didelphimorphia", "Diprotodontia", "Microbiotheria",
            "Notoryctemorphia", "Paucituberculata", "Peramelemorphia",
          ],
        },
      },
      { id: "mammal.other", nameTh: "สัตว์เลี้ยงลูกด้วยนมอื่นๆ" },
    ],
    fallback: "mammal.other",
  },
  {
    id: "bird",
    nameTh: "นก",
    weight: 14,
    phase: 1,
    archetype: "BIRD",
    match: { class: ["Aves"] },
    subgroups: [
      { id: "bird.raptor", nameTh: "นกล่าเหยื่อ", match: { order: ["Accipitriformes", "Cathartiformes", "Falconiformes", "Strigiformes"] } },
      { id: "bird.parrot", nameTh: "นกแก้ว", match: { order: ["Psittaciformes"] } },
      { id: "bird.hornbill_woodpecker", nameTh: "นกเงือกและนกหัวขวาน", match: { order: ["Bucerotiformes", "Piciformes"] } },
      {
        id: "bird.waterbird",
        nameTh: "นกน้ำและนกทะเล",
        match: {
          order: [
            "Anseriformes", "Charadriiformes", "Ciconiiformes", "Gaviiformes", "Gruiformes",
            "Pelecaniformes", "Phaethontiformes", "Phoenicopteriformes", "Podicipediformes",
            "Procellariiformes", "Sphenisciformes", "Suliformes",
          ],
        },
      },
      { id: "bird.passerine", nameTh: "นกเกาะคอน", match: { order: ["Passeriformes"] } },
      { id: "bird.other", nameTh: "นกอื่นๆ" },
    ],
    fallback: "bird.other",
  },
  {
    id: "reptile",
    nameTh: "สัตว์เลื้อยคลาน",
    weight: 10,
    phase: 1,
    archetype: "REPTILE",
    match: {
      class: ["Reptilia"],
      order: ["Crocodylia", "Rhynchocephalia", "Sphenodontia", "Squamata", "Testudinata", "Testudines"],
    },
    subgroups: [
      { id: "reptile.snake", nameTh: "งู", match: { family: SNAKE_FAMILIES } },
      { id: "reptile.turtle", nameTh: "เต่า", match: { order: ["Testudinata", "Testudines"] } },
      { id: "reptile.crocodile", nameTh: "จระเข้", match: { order: ["Crocodylia"] } },
      { id: "reptile.lizard", nameTh: "กิ้งก่าและตุ๊กแก" },
    ],
    fallback: "reptile.lizard",
  },
  {
    id: "amphibian",
    nameTh: "สัตว์สะเทินน้ำสะเทินบก",
    weight: 8,
    phase: 1,
    archetype: "AMPHIBIAN",
    match: { class: ["Amphibia"], order: ["Anura", "Caudata", "Gymnophiona", "Urodela"] },
    subgroups: [
      { id: "amphibian.frog", nameTh: "กบและคางคก", match: { order: ["Anura"] } },
      { id: "amphibian.salamander", nameTh: "ซาลาแมนเดอร์และกระท่าง", match: { order: ["Caudata", "Urodela"] } },
      { id: "amphibian.caecilian", nameTh: "เขียดงู", match: { order: ["Gymnophiona"] } },
    ],
    fallback: "amphibian.frog",
  },
  {
    id: "fish",
    nameTh: "ปลา",
    weight: 14,
    phase: 1,
    archetype: "FISH",
    match: {
      class: [
        "Actinopteri", "Actinopterygii", "Cephalaspidomorphi", "Chondrichthyes", "Cladistia",
        "Coelacanthi", "Dipneusti", "Elasmobranchii", "Holocephali", "Hyperoartia", "Myxini",
        "Petromyzonti", "Petromyzontida", "Teleostei",
      ],
      // ปลาซีลาแคนท์และปลาปอด (ไม่ใช้ class Sarcopterygii เพราะบางระบบรวมสัตว์สี่ขาไว้ด้วย)
      order: ["Ceratodontiformes", "Coelacanthiformes", "Lepidosireniformes"],
    },
    subgroups: [
      { id: "fish.shark_ray", nameTh: "ปลาฉลามและกระเบน", match: { class: ["Chondrichthyes", "Elasmobranchii", "Holocephali"] } },
      { id: "fish.catfish", nameTh: "ปลาหนัง", match: { order: ["Siluriformes"] } },
      { id: "fish.carp", nameTh: "ปลาตะเพียนและพวกพ้อง", match: { order: ["Characiformes", "Cypriniformes", "Gonorynchiformes", "Gymnotiformes"] } },
      { id: "fish.perch", nameTh: "ปลากะพงและพวกพ้อง", match: { order: PERCOMORPH_ORDERS } },
      { id: "fish.other", nameTh: "ปลาอื่นๆ" },
    ],
    fallback: "fish.other",
  },
  {
    id: "insect",
    nameTh: "แมลง",
    weight: 20,
    phase: 1,
    archetype: "INSECT",
    match: { class: ["Insecta"] },
    subgroups: [
      {
        id: "insect.ant_termite",
        nameTh: "มดและปลวก",
        match: { family: ["Formicidae", ...TERMITE_FAMILIES] },
        archetype: "SOCIAL_INSECT",
      },
      { id: "insect.beetle", nameTh: "ด้วง", match: { order: ["Coleoptera"] } },
      { id: "insect.butterfly", nameTh: "ผีเสื้อและมอธ", match: { order: ["Lepidoptera"] } },
      { id: "insect.fly", nameTh: "แมลงวันและยุง", match: { order: ["Diptera"] } },
      { id: "insect.bee_wasp", nameTh: "ผึ้งและต่อ", match: { order: ["Hymenoptera"] } },
      { id: "insect.bug", nameTh: "มวนและจักจั่น", match: { order: ["Hemiptera"] } },
      { id: "insect.grasshopper", nameTh: "ตั๊กแตนและจิ้งหรีด", match: { order: ["Orthoptera"] } },
      { id: "insect.dragonfly", nameTh: "แมลงปอ", match: { order: ["Odonata"] } },
      { id: "insect.other", nameTh: "แมลงอื่นๆ" },
    ],
    fallback: "insect.other",
  },
  {
    id: "arachnid",
    nameTh: "แมง",
    weight: 6,
    phase: 3,
    archetype: "ARACHNID",
    match: { class: ["Arachnida"] },
    subgroups: [
      { id: "arachnid.spider", nameTh: "แมงมุม", match: { order: ["Araneae"] } },
      { id: "arachnid.scorpion", nameTh: "แมงป่อง", match: { order: ["Scorpiones"] } },
      {
        id: "arachnid.mite_tick",
        nameTh: "ไรและเห็บ",
        match: { order: ["Holothyrida", "Ixodida", "Mesostigmata", "Opilioacarida", "Sarcoptiformes", "Trombidiformes"] },
      },
      { id: "arachnid.other", nameTh: "แมงอื่นๆ" },
    ],
    fallback: "arachnid.other",
  },
  {
    id: "crustacean",
    nameTh: "กุ้ง ปู และพวกพ้อง",
    weight: 5,
    phase: 3,
    archetype: "CRUSTACEAN",
    match: {
      class: [
        "Branchiopoda", "Branchiura", "Cephalocarida", "Copepoda", "Hexanauplia", "Ichthyostraca",
        "Malacostraca", "Maxillopoda", "Mystacocarida", "Ostracoda", "Remipedia", "Tantulocarida",
        "Thecostraca",
      ],
    },
    subgroups: [
      { id: "crustacean.decapod", nameTh: "กุ้งและปู", match: { order: ["Decapoda"] } },
      { id: "crustacean.other", nameTh: "สัตว์พวกกุ้งปูอื่นๆ" },
    ],
    fallback: "crustacean.other",
  },
  {
    id: "mollusc",
    nameTh: "หอยและหมึก",
    weight: 5,
    phase: 3,
    archetype: "MOLLUSC",
    match: { phylum: ["Mollusca"] },
    subgroups: [
      { id: "mollusc.cephalopod", nameTh: "หมึกและหอยงวงช้าง", match: { class: ["Cephalopoda"] } },
      { id: "mollusc.gastropod", nameTh: "หอยฝาเดียวและทาก", match: { class: ["Gastropoda"] } },
      { id: "mollusc.bivalve", nameTh: "หอยสองฝา", match: { class: ["Bivalvia"] } },
      { id: "mollusc.other", nameTh: "หอยอื่นๆ" },
    ],
    fallback: "mollusc.other",
  },
  {
    id: "myriapod",
    nameTh: "ตะขาบและกิ้งกือ",
    weight: 1,
    phase: 3,
    archetype: "MYRIAPOD",
    match: { class: ["Chilopoda", "Diplopoda", "Pauropoda", "Symphyla"] },
    subgroups: [
      { id: "myriapod.centipede", nameTh: "ตะขาบ", match: { class: ["Chilopoda"] } },
      { id: "myriapod.millipede", nameTh: "กิ้งกือ", match: { class: ["Diplopoda"] } },
      { id: "myriapod.other", nameTh: "สัตว์หลายขาอื่นๆ" },
    ],
    fallback: "myriapod.other",
  },
  {
    id: "worm",
    nameTh: "หนอน",
    weight: 1,
    phase: 3,
    archetype: "WORM",
    match: { phylum: ["Annelida", "Nematoda", "Platyhelminthes"] },
    subgroups: [
      { id: "worm.annelid", nameTh: "ไส้เดือนและปลิง", match: { phylum: ["Annelida"] } },
      { id: "worm.nematode", nameTh: "หนอนตัวกลม", match: { phylum: ["Nematoda"] } },
      { id: "worm.flatworm", nameTh: "หนอนตัวแบน", match: { phylum: ["Platyhelminthes"] } },
    ],
    fallback: "worm.annelid",
  },
  {
    id: "cnidarian",
    nameTh: "ปะการัง แมงกะพรุน และดอกไม้ทะเล",
    weight: 1,
    phase: 3,
    // แมงกะพรุนมีช่วงเกาะติดที่ (โพลิป) ก่อนลอยเป็นตัวเต็มวัย จึงใช้ SESSILE ร่วมกับปะการัง
    archetype: "SESSILE",
    match: { phylum: ["Cnidaria"] },
    subgroups: [
      { id: "cnidarian.coral", nameTh: "ปะการังและดอกไม้ทะเล", match: { class: ["Anthozoa", "Hexacorallia", "Octocorallia"] } },
      { id: "cnidarian.jellyfish", nameTh: "แมงกะพรุน", match: { class: ["Cubozoa", "Hydrozoa", "Scyphozoa", "Staurozoa"] } },
    ],
    fallback: "cnidarian.coral",
  },
  {
    id: "echinoderm",
    nameTh: "ดาวทะเลและเม่นทะเล",
    weight: 1,
    phase: 3,
    archetype: "ECHINODERM",
    match: { phylum: ["Echinodermata"] },
    subgroups: [
      { id: "echinoderm.starfish", nameTh: "ดาวทะเล", match: { class: ["Asteroidea"] } },
      { id: "echinoderm.urchin", nameTh: "เม่นทะเล", match: { class: ["Echinoidea"] } },
      { id: "echinoderm.cucumber", nameTh: "ปลิงทะเล", match: { class: ["Holothuroidea"] } },
      { id: "echinoderm.other", nameTh: "ดาวเปราะและพลับพลึงทะเล" },
    ],
    fallback: "echinoderm.other",
  },
];

/** กลุ่มนี้เปิดให้สุ่มแล้วหรือยัง */
export function isGroupEnabled(group: GroupDef): boolean {
  return ENABLED_ARCHETYPES.has(group.archetype);
}
